import { logicIndexToSpriteIndex } from 'helpers/sharedHelpers';
import { PIECE_TYPES } from 'helpers/constants';
import {
  BLUE_PIECES,
  CHARACTERS,
  RED_PIECES,
  RED_PIECE_RANK_INDEXES,
  FAYETTE_MOVEMENT_PROPERTIES
} from './constants';

export const findTypeAndColor = (type, color) => piece => {
  return (
    getPieceTypeByIndex(piece.tile.index) === type && piece.color === color
  );
};

export function getPieceTypeByIndex(pieceIndex) {
  switch (pieceIndex) {
    case CHARACTERS.gma:
    case CHARACTERS.sam:
      return PIECE_TYPES[0];
    case CHARACTERS.raide:
      return PIECE_TYPES[1];
    case CHARACTERS.uncle:
      return PIECE_TYPES[3];
    case CHARACTERS.angryQueen:
      return PIECE_TYPES[4];
    default:
      break;
  }
  // for blue
  if (BLUE_PIECES.includes(pieceIndex)) {
    let typeIndex = (pieceIndex - 271) % 6;
    return PIECE_TYPES[typeIndex];
  }
  if (RED_PIECES.includes(pieceIndex)) {
    const rankIndex = RED_PIECE_RANK_INDEXES[RED_PIECES.indexOf(pieceIndex)];
    return PIECE_TYPES[rankIndex];
  }
}

export function setActivePieceToRank(rank, piece) {
  const newRankSpriteIndex = logicIndexToSpriteIndex(rank);
  const tile = piece.tile.tilemap.putTileAt(
    rank,
    piece.tile.x,
    piece.tile.y,
    true,
    'logic_layer'
  );
  piece.tile = tile;
  tile.occupant = piece;
  piece.setFrame(newRankSpriteIndex);
}

export function canPieceEquipItem(piece, item) {
  if (!item) {
    return false;
  }
  const rank = PIECE_TYPES[piece.rank];
  const classMatchesRank = rank.toLowerCase() === item.class;
  const classIsAny = item.class === 'any';
  const bothFayette =
    item.class === 'fayette' && piece.name.toLowerCase() === 'fayette';
  if (classMatchesRank || classIsAny || bothFayette) {
    return true;
  }
  return false;
}

const idleAnimKeys = {
  271: {
    texture: 'port_Pp_idle',
    key: 'port_Pp_idle_anim',
    anim: null
  },
  272: {
    texture: 'port_Hp_idle',
    key: 'port_Hp_idle_anim',
    anim: null
  },
  273: {
    texture: 'port_Bp_idle',
    key: 'port_Bp_idle_anim',
    anim: null
  },
  274: {
    texture: 'port_Rp_idle',
    key: 'port_Rp_idle_anim',
    anim: null
  },
  275: {
    texture: 'port_Qp_idle',
    key: 'port_Qp_idle_anim',
    anim: null
  },
  276: {
    texture: 'port_Kp_idle',
    key: 'port_Kp_idle_anim',
    anim: null
  },
  283: {
    texture: 'port_fayette_idle',
    key: 'port_fayette_idle_anim',
    anim: null
  },
  307: {
    texture: 'port_P_idle',
    key: 'port_P_idle_anim',
    anim: null
  },
  308: {
    texture: 'port_H_idle',
    key: 'port_H_idle_anim',
    anim: null
  },
  309: {
    texture: 'port_B_idle',
    key: 'port_B_idle_anim',
    anim: null
  },
  310: {
    texture: 'port_R_idle',
    key: 'port_R_idle_anim',
    anim: null
  },
  311: {
    texture: 'port_Q_idle',
    key: 'port_Q_idle_anim',
    anim: null
  },
  312: {
    texture: 'port_K_idle',
    key: 'port_K_idle_anim',
    anim: null
  },
  235: {
    texture: 'port_gma_idle',
    key: 'port_gma_idle_anim',
    anim: null
  },
  236: {
    texture: 'port_sam_idle',
    key: 'port_sam_idle_anim',
    anim: null
  },
  237: {
    texture: 'port_raide_idle',
    key: 'port_raide_idle_anim',
    anim: null
  },
  238: {
    texture: 'port_Qgold_idle',
    key: 'port_Qgold_idle_anim',
    anim: null
  },
  239: {
    texture: 'port_Uncle_idle',
    key: 'port_Uncle_idle_anim',
    anim: null
  },
  241: {
    texture: 'port_Kgold_idle',
    key: 'port_Kgold_idle_anim',
    anim: null
  }
};

function canApplyTexture(scene, gameObject, textureKey) {
  return (
    scene &&
    scene.sys &&
    scene.textures &&
    (!textureKey || scene.textures.exists(textureKey)) &&
    gameObject &&
    gameObject.scene &&
    gameObject.scene.sys &&
    !gameObject.destroyed &&
    gameObject.active !== false &&
    typeof gameObject.setTexture === 'function'
  );
}

export function checkAndApplyIdleAnimation(scene, piece, pieceData) {
  if (!scene || !piece || !pieceData) return;

  const isGma = pieceData.pieceIndex === 235;
  const isGmaBasement = scene.levelData.name === "Grandma's House, ???";
  const isWorldScene = scene.sys.config === 'WorldScene';
  const isStoryScene = scene.sys.config === 'StoryLevelScene';
  const isMPScene = scene.sys.config === 'MultiplayerLevelScene';
  const isCastleScene = scene.sys.config === 'CastleScene';
  const isPlainGameScene = scene.sys.config === 'GameScene';
  const idleAnimsActive =
    isPlainGameScene ||
    (isWorldScene && scene.battlingInWorld) ||
    (isStoryScene &&
      !scene.replay &&
      scene.levelData.combat &&
      scene.battlingInStory) ||
    isMPScene ||
    isGmaBasement ||
    isCastleScene;
  if (idleAnimsActive) {
    const idleAnim = idleAnimKeys[pieceData.pieceIndex];
    if (idleAnim) {
      if (!canApplyTexture(scene, piece, idleAnim.texture)) return;

      piece.setTexture(idleAnim.texture);
      piece.pieceIndex = pieceData.pieceIndex;
      if (!idleAnim.anim) {
        idleAnim.anim = scene.anims.create({
          key: idleAnim.key,
          frames: scene.anims.generateFrameNumbers(idleAnim.texture),
          frameRate: isGma ? 4 : 8,
          yoyo: false,
          repeat: -1
        });
      }
      piece.anims.load(idleAnim.key);
      piece.anims.play(idleAnim.key);
    }
  }
}

const UNCLE_MOVEMENT_SPRITE_FRAMES_BY_DIRECTION = {
  [-1]: { 0: 3 },
  0: { [-1]: 0, 1: 2 },
  1: { 0: 1 }
};

const BISHOP_MOVEMENT_SPRITE_FRAMES_BY_DIRECTION = {
  [-1]: { [-1]: 0, 1: 3 },
  1: { [-1]: 1, 1: 2 }
};

const movementDataByPieceIndex = {
  283: {
    texture: 'fayette_movement',
    animationKey: 'fayette-movement-main'
  },
  239: {
    texture: 'uncle_movement',
    frameData: UNCLE_MOVEMENT_SPRITE_FRAMES_BY_DIRECTION
  },
  273: {
    texture: 'bishop_movement',
    frameData: BISHOP_MOVEMENT_SPRITE_FRAMES_BY_DIRECTION
  }
};

export const swapPieceSpriteForMovementSprite = (scene, piece, direction) => {
  const movementData = movementDataByPieceIndex[piece.tile.index];
  const previousTexture = piece.texture.key;

  if (piece.tile.index === 283) {
    if (
      !scene.fayetteMovingSprite ||
      !piece.parentContainer.exists(scene.fayetteMovingSprite)
    ) {
      scene.fayetteMovingSprite = scene.add.sprite(0, 0, movementData.texture);
      const fayetteMovementConfig = {
        key: movementData.animationKey,
        frames: scene.anims.generateFrameNumbers(movementData.texture),
        frameRate: FAYETTE_MOVEMENT_PROPERTIES.movementAnimationFramerate,
        yoyo: false,
        repeat: 0
      };
      scene.fayetteWalkingAnim = scene.anims.create(fayetteMovementConfig);
      scene.fayetteMovingSprite.anims.load(movementData.animationKey);
    }

    scene.fayetteMovingSprite.setAlpha(1);
    piece.parentContainer.add(scene.fayetteMovingSprite);
    piece.parentContainer.sendToBack(scene.fayetteMovingSprite);

    piece.setAlpha(0);

    if (scene.fayetteMovingSprite.anims.isPlaying) {
      scene.fayetteMovingSprite.anims.restart();
    } else {
      scene.fayetteMovingSprite.anims.play(movementData.animationKey);
    }
    scene.fayetteMovingSprite.on('animationcomplete', (anim, frame) => {
      scene.fayetteMovingSprite.setAlpha(0);
      piece.setAlpha(1);
    });
  } else {
    const frame = movementData.frameData[direction[0]][direction[1]];
    if (frame || frame === 0) {
      piece.setTexture(movementData.texture);
      piece.setFrame(frame);
    }
  }

  return previousTexture;
};
